import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { PROJECTS } from './constants';
import fleekLogo from '@assets/fleek-yellow-logo.svg';
import fancodeLogo from '@assets/fancode-icon.svg';
import flipkartLogo from '@assets/flipkart-icon.svg';
import { SectionContainer, Container, SectionHeader, SectionTitle, Grid, Content, Card, CardImage, CardContent, CompanyLogo, CardTitle, Tag, Description, Metrics, MetricItem, } from './Projects.styles';
import { useAnalytics } from '../../../hooks/useAnalytics';
// Map company names to imported logos
const companyLogos = {
    'Fleek': fleekLogo,
    'FanCode': fancodeLogo,
    'Flipkart': flipkartLogo,
};
const Projects = () => {
    const { trackSectionView, trackProjectView } = useAnalytics();
    const sectionRef = useRef(null);
    const viewedProjects = useRef(new Set());
    // Track section view when it comes into viewport
    useEffect(() => {
        if (typeof window === 'undefined' || !sectionRef.current)
            return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    trackSectionView('projects');
                    observer.disconnect();
                }
            });
        }, { threshold: 0.3 });
        observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, [trackSectionView]);
    // Track individual project views when they scroll into view
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const projectCards = document.querySelectorAll('[data-project-id]');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const projectId = entry.target.getAttribute('data-project-id');
                    const projectTitle = entry.target.getAttribute('data-project-title');
                    if (projectId && projectTitle && !viewedProjects.current.has(projectId)) {
                        viewedProjects.current.add(projectId);
                        trackProjectView(projectId, projectTitle);
                    }
                }
            });
        }, { threshold: 0.5 });
        projectCards.forEach((card) => observer.observe(card));
        return () => observer.disconnect();
    }, [trackProjectView]);
    const handleImageError = (e) => {
        const target = e.currentTarget;
        // Try PNG fallback if WebP fails
        if (target.src.endsWith('.webp')) {
            target.src = target.src.replace('.webp', '.png');
            return;
        }
        // If PNG also fails, show placeholder
        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180"%3E%3Crect width="100%25" height="100%25" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-family="Arial" font-size="14" fill="%239ca3af"%3EImage not available%3C/text%3E%3C/svg%3E';
    };
    return (_jsx(SectionContainer, { id: "projects", ref: sectionRef, children: _jsxs(Container, { children: [_jsx(SectionHeader, { children: _jsx(SectionTitle, { children: "Featured Projects" }) }), _jsx(Grid, { children: _jsx(Content, { children: PROJECTS.map((project) => (_jsxs(Card, { "data-project-id": project.id, "data-project-title": project.title, children: [_jsx(CardImage, { src: project.image, alt: `${project.title} - ${project.company || 'Project'}`, loading: "lazy", decoding: "async", onError: handleImageError }), project.company && project.companyLogo && (_jsx(CompanyLogo, { src: companyLogos[project.company] || project.companyLogo, alt: `${project.company} logo` })), _jsxs(CardContent, { children: [_jsx(CardTitle, { children: project.title }), _jsx("div", { children: project.tags.map((tag) => (_jsx(Tag, { children: tag }, tag))) }), project.description && (_jsx(Description, { children: project.description })), _jsx(Metrics, { children: project.metrics.map((metric, idx) => (_jsx(MetricItem, { children: metric }, idx))) })] })] }, project.id))) }) })] }) }));
};
export default Projects;
